'use client'

import * as React from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import { format, parse, parseISO, isValid } from 'date-fns'
import { nl } from 'date-fns/locale'
import { cn } from '../lib/utils'
import { Button } from './button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Input } from './input'

export interface DateTimePickerProps {
  value: string | undefined
  onChange: (value: string | undefined) => void
  placeholder?: string
  disabled?: boolean
  fromDate?: Date
  toDate?: Date
  minuteStep?: number
  required?: boolean
  className?: string
  id?: string
  'aria-label'?: string
  /**
   * Toon ISO-weeknummers links van de dagen in de pop-up-kalender. Default
   * `false` zodat bestaande callers ongewijzigd blijven; werkbon-create-modal
   * en andere planner-flows zetten dit op `true`.
   *
   * Ronde 4 W2 (spec 02 §4.2.2) — voorheen verdween de prop bij
   * `DateTimePicker`, omdat alleen `DatePicker` en `Calendar` hem doorgaven.
   */
  showWeekNumbers?: boolean
}

function isoToDate(iso: string | undefined): Date | undefined {
  if (!iso) return undefined
  const d = parseISO(iso)
  return isValid(d) ? d : undefined
}

function dateToIso(d: Date): string {
  return format(d, "yyyy-MM-dd'T'HH:mm")
}

function roundMinutes(d: Date, step: number): Date {
  const minutes = d.getMinutes()
  const rounded = Math.round(minutes / step) * step
  const out = new Date(d)
  out.setMinutes(rounded, 0, 0)
  return out
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = 'Kies datum + tijd',
  disabled,
  fromDate,
  toDate,
  minuteStep = 15,
  required,
  className,
  id,
  'aria-label': ariaLabel,
  showWeekNumbers = false,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const selected = isoToDate(value)
  const timeStr = selected ? format(selected, 'HH:mm') : ''

  const handleDateSelect = (d: Date | undefined) => {
    if (!d) {
      onChange(undefined)
      return
    }
    const base = selected ? new Date(selected) : roundMinutes(new Date(), minuteStep)
    base.setFullYear(d.getFullYear(), d.getMonth(), d.getDate())
    onChange(dateToIso(base))
  }

  const handleTimeChange = (newTime: string) => {
    if (!/^\d{2}:\d{2}$/.test(newTime)) return
    const parsedTime = parse(newTime, 'HH:mm', new Date())
    if (!isValid(parsedTime)) return
    const base = selected ? new Date(selected) : new Date()
    base.setHours(parsedTime.getHours(), parsedTime.getMinutes(), 0, 0)
    onChange(dateToIso(base))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-required={required}
          className={cn(
            'w-full justify-start text-left font-normal',
            !selected && 'text-[#5C5C58]',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selected
            ? `${format(selected, 'd MMM yyyy', { locale: nl })} • ${format(selected, 'HH:mm')}`
            : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        // De kalender mag nooit buiten het scherm vallen: in een modal is de
        // pagina erachter vergrendeld, dus wat onder de rand valt is
        // onbereikbaar. Begrens op de ruimte die Radix meet en scroll daarbinnen.
        className="w-auto max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-0"
        align="start"
        collisionPadding={8}
        // De scroll-vergrendeling van een omliggende Dialog blokkeert wiel/touch
        // in deze geportalde popover; laat die events hier niet doorbubbelen.
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleDateSelect}
          fromDate={fromDate}
          toDate={toDate}
          showWeekNumbers={showWeekNumbers}
          initialFocus
        />
        <div className="border-t border-[#CED0C8] p-3 flex items-center gap-2">
          <label className="text-sm text-[#1A1A18]">Tijd</label>
          <Input
            type="time"
            step={minuteStep * 60}
            value={timeStr}
            onChange={(e) => handleTimeChange(e.target.value)}
            className="w-32"
            disabled={!selected}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => setOpen(false)}
          >
            Klaar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
